import { useRef } from 'react'
import { useLocation } from 'react-router-dom-v5-compat'
import { gql } from '@apollo/client'
import { useProductLaunchModalCMS } from 'features/StartupModals/CMSProductLaunchModal/hooks/useProductLaunchModalCMS'
import { useProductTour } from 'features/StartupModals/hooks/useProductTour/useProductTour'
import { usePushAppModal } from 'features/StartupModals/PushAppModal/hooks/usePushAppModal'

import { useCustomerProductTourStatus } from 'hooks/useCustomerProductTourStatus'
import { useLoggedInUser } from 'hooks/useLoggedInUser'
import { ROUTE } from 'const/routes'
import { ConnectionStatus, CustomerRole } from 'types/gql-global-types'

import {
  UseStartupModalsConnectionFragment,
  UseStartupModalsEntitySavingsAccountProductFragment,
} from './useStartupModals.generated'

type UseStartupModalsProps = {
  savingsAccountProduct: UseStartupModalsEntitySavingsAccountProductFragment | null
  xeroBankFeed: UseStartupModalsConnectionFragment | undefined
  xeroPaymentServices: UseStartupModalsConnectionFragment | undefined
}

export const useStartupModals = ({
  savingsAccountProduct,
  xeroBankFeed,
  xeroPaymentServices,
}: UseStartupModalsProps) => {
  const { userRole } = useLoggedInUser()

  const location = useLocation()

  const { shouldShowAdminWelcome } = useProductTour()
  const { shouldShowModal: shouldShowInvoiceApril } =
    useProductLaunchModalCMS('showInvoiceApril')

  const { showCorporateCardsAdminWelcome, showSavingsAccountWelcome } =
    useCustomerProductTourStatus()
  const { shouldShowPushAppModal } = usePushAppModal()
  const isAdmin = userRole === CustomerRole.ADMIN

  const hasErrorInBankFeeds =
    xeroBankFeed?.status === ConnectionStatus.CONNECTED_WITH_ERROR
  const hasErrorInPaymentService =
    xeroPaymentServices?.status === ConnectionStatus.CONNECTED_WITH_ERROR

  const initialSavingsAccountWelcome = useRef(showSavingsAccountWelcome)

  return {
    showSetupFlow: shouldShowAdminWelcome,
    showSavingsAccountWelcome:
      isAdmin &&
      initialSavingsAccountWelcome.current &&
      !!savingsAccountProduct,
    showCorporateCardStartup:
      isAdmin &&
      showCorporateCardsAdminWelcome &&
      location.pathname.includes(ROUTE.PORTAL_OVERVIEW),
    showXeroErrorModal:
      isAdmin &&
      location.pathname.includes(ROUTE.PORTAL_OVERVIEW) &&
      (hasErrorInBankFeeds || hasErrorInPaymentService),
    showPushAppModal: shouldShowPushAppModal,
    showInvoiceApril:
      shouldShowInvoiceApril &&
      location.pathname.includes(ROUTE.PORTAL_OVERVIEW),
  }
}

useStartupModals.fragments = {
  Connection: gql`
    fragment UseStartupModalsConnectionFragment on Connection {
      status
    }
  `,
  EntitySavingsAccountProduct: gql`
    fragment UseStartupModalsEntitySavingsAccountProductFragment on EntitySavingsAccountProduct {
      id
    }
  `,
}
